import React, { useEffect, useState } from "react";
import { X, ZoomIn, ZoomOut } from "lucide-react";

/**
 * Lightbox plein écran pour aperçus (image ou PDF pages rasterisées).
 * `pages` : liste d'URLs signées (déjà autorisées côté serveur).
 */
export default function PreviewLightbox({ open, pages, initialIndex = 0, onClose, title }) {
  const [idx, setIdx] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    if (open) {
      setIdx(initialIndex);
      setZoom(1);
    }
  }, [open, initialIndex]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setIdx((i) => Math.min(i + 1, (pages?.length || 1) - 1));
      if (e.key === "ArrowLeft") setIdx((i) => Math.max(i - 1, 0));
      if (e.key === "+") setZoom((z) => Math.min(z + 0.25, 3));
      if (e.key === "-") setZoom((z) => Math.max(z - 0.25, 0.5));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, pages, onClose]);

  if (!open) return null;
  const current = pages?.[idx];

  return (
    <div
      className="fixed inset-0 bg-black/85 backdrop-blur-sm z-[100] flex flex-col"
      data-testid="preview-lightbox"
      onContextMenu={(e) => e.preventDefault()}
    >
      <div className="flex items-center justify-between px-4 md:px-8 py-3 text-[#FAF8F5]">
        <div className="text-xs font-mono uppercase tracking-[0.16em]">
          {title || "Aperçu"} {pages?.length > 1 && `· page ${idx + 1}/${pages.length}`}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setZoom((z) => Math.max(z - 0.25, 0.5))}
            className="p-2 hover:bg-white/10 rounded"
            aria-label="Zoom arrière"
            data-testid="lightbox-zoom-out"
          >
            <ZoomOut size={18} />
          </button>
          <span className="text-xs font-mono w-12 text-center">
            {Math.round(zoom * 100)}%
          </span>
          <button
            type="button"
            onClick={() => setZoom((z) => Math.min(z + 0.25, 3))}
            className="p-2 hover:bg-white/10 rounded"
            aria-label="Zoom avant"
            data-testid="lightbox-zoom-in"
          >
            <ZoomIn size={18} />
          </button>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded ml-2"
            aria-label="Fermer"
            data-testid="lightbox-close"
          >
            <X size={20} />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto flex items-center justify-center p-4 md:p-8 select-none">
        {current && (
          <img
            src={current.url}
            alt={`Aperçu ${idx + 1}`}
            className="max-w-none shadow-2xl bg-white"
            style={{
              transform: `scale(${zoom})`,
              transformOrigin: "center center",
              transition: "transform 120ms ease-out",
              pointerEvents: "none",
            }}
            draggable={false}
            data-testid="lightbox-image"
          />
        )}
      </div>

      {pages?.length > 1 && (
        <div className="flex items-center justify-center gap-4 py-3 text-[#FAF8F5]">
          <button
            type="button"
            onClick={() => setIdx((i) => Math.max(i - 1, 0))}
            disabled={idx === 0}
            className="px-3 py-1 border border-white/20 text-xs font-mono uppercase tracking-[0.14em] disabled:opacity-30"
            data-testid="lightbox-prev"
          >
            ← Précédente
          </button>
          <button
            type="button"
            onClick={() => setIdx((i) => Math.min(i + 1, pages.length - 1))}
            disabled={idx === pages.length - 1}
            className="px-3 py-1 border border-white/20 text-xs font-mono uppercase tracking-[0.14em] disabled:opacity-30"
            data-testid="lightbox-next"
          >
            Suivante →
          </button>
        </div>
      )}
    </div>
  );
}
